import type { Vehicle } from '@/state/types';
import { VEHICLE_SPECS } from './vehicles';
import { VEHICLE_REPAIR_COST_MULTIPLIER } from './resources';

/**
 * Phase 6.1 — Werkstatt / Reparatur tunables.
 */

/** Below this condition the vehicle is undispatchable. */
export const REPAIR_DISABLE_THRESHOLD = 0.25;
/** Below this condition the vehicle drives at REPAIR_SLOW_FACTOR speed. */
export const REPAIR_SLOW_THRESHOLD = 0.5;
/** Speed multiplier applied while between SLOW and DISABLE thresholds. */
export const REPAIR_SLOW_FACTOR = 0.9;

/** Standard (cheap, slow) repair duration in real-time milliseconds. */
export const STANDARD_REPAIR_DURATION_MS = 5 * 60_000;
/** Sofort (premium, fast) repair duration in real-time milliseconds. */
export const INSTANT_REPAIR_DURATION_MS = 30_000;

export type RepairMode = 'standard' | 'instant';

/**
 * Base repair cost — proportional to damage taken.
 *   `vehicle.cost × 0.4 × (1 - condition)`
 * → Full vehicle replacement is 100 %, fully damaged repair is 40 %.
 *   Lightly damaged costs scale linearly down toward 0.
 */
export function baseRepairCost(vehicle: Vehicle): number {
  const cost = VEHICLE_SPECS[vehicle.type].cost;
  const damage = Math.max(0, Math.min(1, 1 - vehicle.condition));
  return Math.round(cost * 0.4 * damage);
}

export function repairCostFor(vehicle: Vehicle, mode: RepairMode): number {
  const base = baseRepairCost(vehicle);
  return mode === 'instant' ? Math.round(base * VEHICLE_REPAIR_COST_MULTIPLIER) : base;
}

export function repairDurationMs(mode: RepairMode): number {
  return mode === 'instant' ? INSTANT_REPAIR_DURATION_MS : STANDARD_REPAIR_DURATION_MS;
}

/** True when the vehicle is currently undispatchable due to low condition. */
export function isCriticalCondition(vehicle: Vehicle): boolean {
  return vehicle.condition <= REPAIR_DISABLE_THRESHOLD;
}

/** True when the vehicle drives at reduced speed but can still respond. */
export function isReducedSpeed(vehicle: Vehicle): boolean {
  return (
    vehicle.condition > REPAIR_DISABLE_THRESHOLD &&
    vehicle.condition < REPAIR_SLOW_THRESHOLD
  );
}

/** Effective speed multiplier, taking condition into account. */
export function effectiveSpeedMultiplier(vehicle: Vehicle): number {
  const spec = VEHICLE_SPECS[vehicle.type];
  if (isCriticalCondition(vehicle)) return spec.speedMultiplier; // shouldn't drive anyway
  if (isReducedSpeed(vehicle)) return spec.speedMultiplier * REPAIR_SLOW_FACTOR;
  return spec.speedMultiplier;
}
